import { Link, useNavigate } from "react-router-dom";
import ProgressBar from "../components/ProgressBar";
import { useState, useEffect } from "react";
import { getSouscriptionType } from '../services/assuranceService';
import { validateAge } from '../utils/validationUtils';
import localforage from "localforage";
import { wait } from "@testing-library/user-event/dist/utils";

// UUIDs pour les types d'assurance
const SOLO_TYPE_UUID = '019beade-d8aa-71da-85e4-afa966b97457';
const MONO_FAMILLE_TYPE_UUID = '019c2845-b537-7d9c-b921-5d56e6002593';
const FAMILLE_TYPE_UUID = '019beade-f87c-73ec-a06f-9d24a4e4dc80';

export default function Step2() {
  const navigate = useNavigate();
  const [isPrincipal, setIsPrincipal] = useState(null);
  const [assuranceType, setAssuranceType] = useState(null);
  const [activityClass, setActivityClass] = useState(null);
  const [types, setTypes] = useState(null);
  const [errors, setErrors] = useState({});

  // Données du souscripteur (pour pré-remplir si isPrincipal = oui)
  const [subscriberData, setSubscriberData] = useState(null);
  const [subscriberPreviews, setSubscriberPreviews] = useState(null);
  const [subscriberFiles, setSubscriberFiles] = useState(null);

  // Formulaire de l'assuré principal
  const [formData, setFormData] = useState({
    nom: '',
    prenoms: '',
    dateNaissance: '',
    sexe: '',
    contact: '',
    adresse: '',
    typePiece: "",
    numeroCNI: "",
    nni: "",
    numeroPiece: "",
    email: '',
    telephone: '',
    whatsapp: '',
    cniRecto: null,
    cniVerso: null,
    photoIdentite: null,
    carteCMU: null,
  });
  const [previews, setPreviews] = useState({
    cniRecto: null,
    cniVerso: null,
    photoIdentite: null,
    carteCMU: null
  });
  // 'carte' = Carte CMU, 'recu' = Reçu d'enrôlement
  const [cmuDocType, setCmuDocType] = useState('carte');

  useEffect(() => {
    // 🔹 Charger types API
    const fetchTypes = async () => {
      try {
        const result = await getSouscriptionType();
        setTypes(result);
      } catch (error) {
        console.error('Erreur chargement types:', error);
      }
    };

    // 🔹 Charger données locales + fichiers
    const loadLocalData = async () => {
      try {
        const savedSubscriber = localStorage.getItem('subscriberData');
        const savedPreviews = localStorage.getItem('subscriberPreviews');
        const savedFiles = await localforage.getItem('subscriberFiles');

        if (savedSubscriber) {
          setSubscriberData(JSON.parse(savedSubscriber));
        }

        if (savedPreviews) {
          setSubscriberPreviews(JSON.parse(savedPreviews));
        }

        if (savedFiles) {
          setSubscriberFiles(savedFiles);

          const rebuiltPreviews = {};

          Object.entries(savedFiles).forEach(([key, file]) => {
            if (file instanceof File) {
              rebuiltPreviews[key] = URL.createObjectURL(file);
            }
          });

          setSubscriberPreviews(rebuiltPreviews);
        }

        // if (savedFiles) {
        //   setSubscriberFiles(savedFiles);
        // }

      } catch (error) {
        console.error('Erreur chargement local:', error);
      }
    };

    // 🔹 Charger préférences simples
    const loadSettings = () => {
      const savedIsPrincipal = localStorage.getItem('isPrincipal');
      const savedAssuranceType = localStorage.getItem('assuredType');
      const savedActivityClass = localStorage.getItem('activityClass');
      const savedCmuDocType = localStorage.getItem('principalCmuDocType');

      if (savedIsPrincipal) setIsPrincipal(savedIsPrincipal);
      if (savedAssuranceType) setAssuranceType(savedAssuranceType);
      if (savedActivityClass) setActivityClass(savedActivityClass);
      if (savedCmuDocType) setCmuDocType(savedCmuDocType);
    };

    // 🔹 Exécution
    fetchTypes();
    loadLocalData();
    loadSettings();

  }, []);

  // Pré-remplir le formulaire si isPrincipal = oui
  useEffect(() => {
    if (isPrincipal === 'yes' && subscriberData) {
      setFormData({
        nom: subscriberData.nom || '',
        prenoms: subscriberData.prenoms || '',
        dateNaissance: subscriberData.date_naissance || '',
        sexe: subscriberData.sexe || '',
        contact: subscriberData.contact || '',
        adresse: subscriberData.adresse || '',
        typePiece: subscriberData.type_piece || '',
        numeroCNI: subscriberData.numero_CNI || '',
        nni: subscriberData.nni || '',
        numeroPiece: subscriberData.numero_piece || '',
        email: subscriberData.email || '',
        telephone: subscriberData.telephone || '',
        whatsapp: subscriberData.whatsapp || '',
        cniRecto: subscriberFiles.cniRecto || null,
        cniVerso: subscriberFiles.cniVerso || null,
        photoIdentite: subscriberFiles.photoIdentite || null,
        carteCMU: subscriberFiles.carteCMU || null,
      });

      if (subscriberPreviews) 
        setPreviews(subscriberPreviews);
    } else if (isPrincipal === 'no') {
      // Réinitialiser le formulaire si non
      setFormData({
        nom: '',
        prenoms: '',
        dateNaissance: '',
        sexe: '',
        contact: '',
        adresse: '',
        typePiece: "",
        numeroCNI: "",
        nni: "",
        numeroPiece: "",
        email: '',
        telephone: '',
        whatsapp: '',
        cniRecto: null,
        cniVerso: null,
        photoIdentite: null,
        carteCMU: null,
      });
      setPreviews({
        cniRecto: null,
        cniVerso: null,
        photoIdentite: null,
        carteCMU: null
      });
    }
  }, [isPrincipal, subscriberData, subscriberPreviews, subscriberFiles]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleChangeCNI = (e) => {
    let value = e.target.value.toUpperCase();

    // Supprimer tout sauf chiffres après CI
    value = value.replace(/^CI/, ''); // retire CI si déjà tapé
    value = value.replace(/\D/g, ''); // garde seulement les chiffres

    // Limiter à 9 chiffres
    value = value.substring(0, 9);

    setFormData(prev => ({ ...prev, numeroCNI: 'CI' + value }));
  };

  const handleChangeNNI = (e) => {
    let value = e.target.value;

    // garde seulement les chiffres
    value = value.replace(/\D/g, ''); 

    // Limiter à 11 chiffres
    value = value.substring(0, 11);

    setFormData(prev => ({ ...prev, nni: value }));
  };

  // const handleFileChange = (e) => {
  //   const { name, files } = e.target;
  //   if (files && files[0]) {
  //     const file = files[0];
  //     const reader = new FileReader();
  //     reader.onloadend = () => {
  //       setPreviews(prev => ({ ...prev, [name]: reader.result }));
  //       if (errors[name]) {
  //         setErrors(prev => ({ ...prev, [name]: '' }));
  //       }
  //     };
  //     reader.readAsDataURL(file);
  //   }
  // }; 

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      const file = files[0];
      setFormData(prev => ({...prev, [name]: file}));
      const previewUrl = URL.createObjectURL(file);

      setPreviews(prev => {
        if (prev[name]) 
          URL.revokeObjectURL(prev[name]);
        
        return {...prev, [name]: previewUrl};
      });

      if (errors[name]) 
        setErrors(prev => ({...prev, [name]: ''}));
    }
  };

  const handleContinue = async (e) => {
    e.preventDefault();
    let newErrors = {};

    if (!isPrincipal) {
      newErrors.isPrincipal = "Veuillez indiquer si le souscripteur est l'assuré principal.";
    }
    if (!assuranceType) {
      newErrors.assuranceType = "Veuillez sélectionner un type d'assurance.";
    }
    if (!activityClass) {
      newErrors.activityClass = "Veuillez sélectionner une classe d'activité.";
    }

    // Validation du formulaire assuré principal si affiché
    if (isPrincipal) {
      if (!formData.nom.trim()) newErrors.nom = 'Le nom est requis.';
      if (!formData.prenoms.trim()) newErrors.prenoms = 'Le(s) prénom(s) est/sont requis.';
      if (!formData.dateNaissance) {
        newErrors.dateNaissance = 'La date de naissance est requise.';
      } else if (!validateAge(formData.dateNaissance, 18, 100)) {
        newErrors.dateNaissance = "L'assuré principal doit avoir au moins 18 ans.";
      }
      if (!formData.sexe) newErrors.sexe = 'Le sexe est requis.';
      if (!formData.telephone.trim()) newErrors.telephone = 'Le téléphone est requis.';
      if (!formData.typePiece.trim()) newErrors.typePiece = 'Le type de pièce est requis.';
      if (!previews.carteCMU) newErrors.carteCMU = 'Le document CMU (carte ou reçu d\'enrôlement) est requis.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Sauvegarder les données
    const principalData = {
      nom: formData.nom,
      prenoms: formData.prenoms,
      date_naissance: formData.dateNaissance,
      sexe: formData.sexe,
      contact: formData.contact,
      adresse: formData.adresse,
      type_piece: formData.typePiece,
      numero_CNI: formData.numeroCNI,
      nni: formData.nni,
      numero_Piece: formData.numeroPiece,
      email: formData.email,
      telephone: formData.telephone,
      whatsapp: formData.whatsapp
    };
    const principalFiles = {
      cniRecto: formData.cniRecto,
      cniVerso: formData.cniVerso,
      photoIdentite: formData.photoIdentite,
      carteCMU: formData.carteCMU
    };

    localStorage.setItem('isPrincipal', isPrincipal);
    localStorage.setItem('assuredType', assuranceType);
    localStorage.setItem('activityClass', activityClass);
    localStorage.setItem('principalData', JSON.stringify(principalData));
    localStorage.setItem('principalPreviews', JSON.stringify(previews));
    localStorage.setItem('principalCmuDocType', cmuDocType);
    await localforage.setItem('principalFiles', principalFiles);

    // Navigation selon le type d'assurance
    if (assuranceType === FAMILLE_TYPE_UUID || assuranceType === MONO_FAMILLE_TYPE_UUID) {
      navigate('/step-3');
    } else {
      localStorage.setItem('hasSpouse', 'false');
      localStorage.setItem('numberOfChildren', '0');
      localStorage.setItem('spouse', JSON.stringify({}));
      localStorage.setItem('children', JSON.stringify([]));
      localforage.setItem('spouseFiles', {});
      localforage.setItem('childrenFiles', {});
      navigate('/step-4');
    }
  };

  return (
    <div className="subscribe py-10 lg:py-16 bg-[#e4eef9]">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <h1 className="text-center text-4xl font-bold">Formulaire de souscription</h1>
        <div className="mt-10">
          <ProgressBar currentStep={2} />
          <div className="mt-8 bg-white p-6 lg:p-10 rounded-xl shadow-sm">
            <h2 className="text-xl font-bold text-secondary mb-6">2. Assuré Principal & Type d'assurance</h2>

            {/* Question: Le souscripteur est-il l'assuré principal? */}
            <div className="mb-6">
              <label className="block font-bold mb-4">Le Souscripteur est-il l'assuré principal ?</label>
              <div className="flex gap-4">
                <label className={`px-6 py-3 rounded-full cursor-pointer transition ${isPrincipal === 'yes' ? 'bg-secondary text-white' : 'border border-gray-800 hover:bg-gray-100'}`}>
                  <input
                    type="radio"
                    name="isPrincipal"
                    value="yes"
                    checked={isPrincipal === 'yes'}
                    onChange={() => {
                      setIsPrincipal('yes');
                      if (errors.isPrincipal) setErrors(prev => ({...prev, isPrincipal: ''}));
                    }}
                    className="sr-only"
                  />
                  Oui
                </label>
                <label className={`px-6 py-3 rounded-full cursor-pointer transition ${isPrincipal === 'no' ? 'bg-secondary text-white' : 'border border-gray-800 hover:bg-gray-100'}`}>
                  <input
                    type="radio"
                    name="isPrincipal"
                    value="no"
                    checked={isPrincipal === 'no'}
                    onChange={() => {
                      setIsPrincipal('no');
                      if (errors.isPrincipal) setErrors(prev => ({...prev, isPrincipal: ''}));
                    }}
                    className="sr-only"
                  />
                  Non
                </label>
              </div>
              {errors.isPrincipal && <p className="text-red-500 text-sm mt-2">{errors.isPrincipal}</p>}
            </div>

            {/* Formulaire de l'assuré principal (affiché si isPrincipal est sélectionné) */}
            {isPrincipal && (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <h3 className="font-bold mb-4">
                  {isPrincipal === 'yes' ? 'Informations de l\'assuré principal (pré-remplies)' : 'Informations de l\'assuré principal'}
                </h3>
                <form className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-bold mb-2">Nom <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        name="nom"
                        value={formData.nom}
                        onChange={handleChange}
                        className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none"
                        placeholder="Nom"
                      />
                      {errors.nom && <p className="text-red-500 text-sm mt-1">{errors.nom}</p>}
                    </div>
                    <div>
                      <label className="block font-bold mb-2">Prénom(s) <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        name="prenoms"
                        value={formData.prenoms}
                        onChange={handleChange}
                        className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none"
                        placeholder="Prénoms"
                      />
                      {errors.prenoms && <p className="text-red-500 text-sm mt-1">{errors.prenoms}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-bold mb-2">Date de Naissance <span className="text-red-500">*</span></label>
                      <input
                        type="date"
                        name="dateNaissance"
                        value={formData.dateNaissance}
                        onChange={handleChange}
                        className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none"
                      />
                      {errors.dateNaissance && <p className="text-red-500 text-sm mt-1">{errors.dateNaissance}</p>}
                    </div>
                    <div>
                      <label className="block font-bold mb-2">Sexe <span className="text-red-500">*</span></label>
                      <div className="flex gap-4 mt-2">
                        <label className={`px-6 py-2 rounded-full cursor-pointer transition ${formData.sexe === 'M' ? 'bg-secondary text-white' : 'border border-gray-800 hover:bg-gray-100'}`}>
                          <input type="radio" name="sexe" value="M" checked={formData.sexe === 'M'} onChange={handleChange} className="sr-only" />
                          Masculin
                        </label>
                        <label className={`px-6 py-2 rounded-full cursor-pointer transition ${formData.sexe === 'F' ? 'bg-secondary text-white' : 'border border-gray-800 hover:bg-gray-100'}`}>
                          <input type="radio" name="sexe" value="F" checked={formData.sexe === 'F'} onChange={handleChange} className="sr-only" />
                          Féminin
                        </label>
                      </div>
                      {errors.sexe && <p className="text-red-500 text-sm mt-1">{errors.sexe}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-bold mb-2">Téléphone <span className="text-red-500">*</span></label>
                      <input
                        type="tel"
                        name="telephone"
                        value={formData.telephone}
                        onChange={handleChange}
                        className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none"
                        placeholder="Téléphone"
                      />
                      {errors.telephone && <p className="text-red-500 text-sm mt-1">{errors.telephone}</p>}
                    </div>
                    <div>
                      <label className="block font-bold mb-2">Numéro WhatsApp</label>
                      <input
                        type="tel"
                        name="whatsapp"
                        value={formData.whatsapp}
                        onChange={handleChange}
                        className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none"
                        placeholder="Numéro WhatsApp"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-bold mb-2">Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none"
                        placeholder="Email"
                      />
                    </div>
                    <div>
                      <label className="block font-bold mb-2">Adresse Géographique</label>
                      <input
                        type="text"
                        name="adresse"
                        value={formData.adresse}
                        onChange={handleChange}
                        className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none"
                        placeholder="Adresse"
                      />
                    </div>
                  </div>

                  {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-bold mb-2">N° CNI *</label>
                      <input
                        type="text"
                        name="numeroCNI"
                        value={formData.numeroCNI}
                        onChange={handleChange}
                        className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none"
                        placeholder="N° CNI"
                      />
                      {errors.numeroCNI && <p className="text-red-500 text-sm mt-1">{errors.numeroCNI}</p>}
                    </div>
                    <div>
                      <label className="block font-bold mb-2">NNI</label>
                      <input
                        type="text"
                        name="nni"
                        value={formData.nni}
                        onChange={handleChange}
                        className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none"
                        placeholder="NNI"
                      />
                    </div>
                  </div> */}

                  <div className={`grid grid-cols-1 ${formData.typePiece === "CNI" ? "md:grid-cols-3" : "md:grid-cols-2"} gap-4`}>
                    <div>
                      <label className="block font-bold mb-2">Type de pièce <span className="text-red-500">*</span></label>
                      <select name="typePiece"
                        value={formData.typePiece}
                        onChange={handleChange}
                        className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none">
                        <option value="">Sélectionner</option>
                        <option value="CNI">Carte Nationale d'Identité</option>
                        <option value="Attestation">Attestation d'identité</option>
                        <option value="Passeport">Passeport</option>
                        <option value="CC">Carte Consulaire</option>
                      </select>
                      {errors.typePiece && <p className="text-red-500 text-sm mt-1">{errors.typePiece}</p>}
                    </div>
                    
                    {formData.typePiece === "CNI" ? (
                      <>
                        <div>
                          <label className="block font-bold mb-2">N° CNI <span className="text-red-500">*</span></label>
                          <input
                            type="text"
                            name="numeroCNI"
                            value={formData.numeroCNI}
                            onChange={handleChangeCNI}
                            maxLength={11}
                            className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none"
                            placeholder="Numéro de CNI"
                          />
                        </div>
                        <div>
                          <label className="block font-bold mb-2">NNI</label>
                          <input
                            type="text"
                            name="nni"
                            value={formData.nni}
                            onChange={handleChangeNNI}
                            maxLength={11}
                            className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none"
                            placeholder="Numéro National d'Identité"
                          />
                        </div>
                      </>
                    ) : (
                      <div>
                        <label className="block font-bold mb-2">N° Pièce <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          name="numeroPiece"
                          value={formData.numeroPiece}
                          onChange={handleChange}
                          className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2 focus:border-primary focus:outline-none"
                          placeholder="Numéro de Pièce"
                        />
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-bold mb-2">Pièce Recto</label>
                      <input type="file" name="cniRecto" accept="image/*" onChange={handleFileChange} className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2" />
                      <p className="text-[#60215b] text-sm mt-1">* La taille du fichier doit être inférieure à 2Mo</p>
                      {previews.cniRecto && <img src={previews.cniRecto} alt="CNI Recto" className="mt-2 h-20 object-contain rounded" />}
                    </div>
                    <div>
                      <label className="block font-bold mb-2">Pièce Verso</label>
                      <input type="file" name="cniVerso" accept="image/*" onChange={handleFileChange} className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2" />
                      <p className="text-[#60215b] text-sm mt-1">* La taille du fichier doit être inférieure à 2Mo</p>
                      {previews.cniVerso && <img src={previews.cniVerso} alt="CNI Verso" className="mt-2 h-20 object-contain rounded" />}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-bold mb-2">Photo d'identité</label>
                      <input type="file" name="photoIdentite" accept="image/*" onChange={handleFileChange} className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2" />
                      <p className="text-[#60215b] text-sm mt-1">* La taille du fichier doit être inférieure à 2Mo</p>
                      {previews.photoIdentite && <img src={previews.photoIdentite} alt="Aperçu identité" className="mt-2 h-20 object-contain rounded" />}
                    </div>
                    <div>
                      <label className="block font-bold mb-2">Document CMU <span className="text-red-500">*</span></label>
                      <div className="flex gap-4 mb-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="cmuDocTypePrincipal"
                            value="carte"
                            checked={cmuDocType === 'carte'}
                            onChange={() => setCmuDocType('carte')}
                          />
                          <span>Carte CMU</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="cmuDocTypePrincipal"
                            value="recu"
                            checked={cmuDocType === 'recu'}
                            onChange={() => setCmuDocType('recu')}
                          />
                          <span>Reçu d'enrôlement</span>
                        </label>
                      </div>
                      <input type="file" name="carteCMU" accept="image/*" onChange={handleFileChange} className="w-full border-2 border-[#B5B8B6] rounded-2xl px-4 py-2" />
                      <p className="text-[#60215b] text-sm mt-1">* La taille du fichier doit être inférieure à 2Mo</p>
                      {previews.carteCMU && (
                        <img
                          src={previews.carteCMU}
                          alt={cmuDocType === 'recu' ? "Reçu d'enrôlement" : "Carte CMU"}
                          className="mt-2 h-20 object-contain rounded"
                        />
                      )}
                      {errors.carteCMU && <p className="text-red-500 text-sm mt-1">{errors.carteCMU}</p>}
                    </div>
                  </div>
                </form>
              </div>
            )}

            {/* Classe d'activité */}
            <div className="mb-6">
              <label className="block font-bold mb-4">Quelle est la classe d'activité de l'assuré principal ?</label>
              <div className="flex flex-wrap gap-4">
                <label className={`px-6 py-2 rounded-full cursor-pointer transition ${activityClass === '1' ? 'bg-secondary text-white' : 'border border-gray-800 hover:bg-gray-100'}`}>
                  <input type="radio" name="activityClass" value="1" checked={activityClass === '1'} className="sr-only" 
                    onChange={() => { 
                      setActivityClass('1'); 
                      if (errors.activityClass) setErrors({...errors, activityClass: ''});
                    }} />
                  Classe 1
                </label>
                <label className={`px-6 py-2 rounded-full cursor-pointer transition ${activityClass === '2' ? 'bg-secondary text-white' : 'border border-gray-800 hover:bg-gray-100'}`}>
                  <input type="radio" name="activityClass" value="2" checked={activityClass === '2'} className="sr-only" 
                    onChange={() => {
                      setActivityClass('2');
                      if (errors.activityClass) setErrors({...errors, activityClass: ''});
                    }} />
                  Classe 2
                </label>
                <label className={`px-6 py-2 rounded-full cursor-pointer transition ${activityClass === '3' ? 'bg-secondary text-white' : 'border border-gray-800 hover:bg-gray-100'}`}>
                  <input type="radio" name="activityClass" value="3" checked={activityClass === '3'} className="sr-only" 
                    onChange={() => {
                      setActivityClass('3');
                      if (errors.activityClass) setErrors({...errors, activityClass: ''});
                    }} />
                  Classe 3
                </label>
                <label className={`px-6 py-2 rounded-full cursor-pointer transition ${activityClass === '4' ? 'bg-secondary text-white' : 'border border-gray-800 hover:bg-gray-100'}`}>
                  <input type="radio" name="activityClass" value="4" checked={activityClass === '4'} className="sr-only"
                    onChange={() => {
                      setActivityClass('4');
                      if (errors.activityClass) setErrors({...errors, activityClass: ''});
                    }} />
                  Classe 4
                </label>
                <label className={`px-6 py-2 rounded-full cursor-pointer transition ${activityClass === '5' ? 'bg-secondary text-white' : 'border border-gray-800 hover:bg-gray-100'}`}>
                  <input type="radio" name="activityClass" value="5" checked={activityClass === '5'} className="sr-only"
                    onChange={() => {
                      setActivityClass('5');
                      if (errors.activityClass) setErrors({...errors, activityClass: ''});
                    }} />
                  Classe 5
                </label>
              </div>
              {errors.activityClass && <p className="text-red-500 text-sm mt-2">{errors.activityClass}</p>}

              {activityClass && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                  {activityClass === '1' ? ( 
                    <p className="text-gray-700">
                      <strong>Classe 1 : </strong> 
                      comptable, secretaire, assistant, directeur, cadre, fonctionnaire, avocat, notaire, huissier, enseignant, architecte, ingenieur,
                      pharmacien, libraire, serveur, hotel, religieux, pasteur, imam, employe maison, commercant, administratif, bureau.
                    </p>
                  ) : activityClass === '2' ? (
                    <p className="text-gray-700">
                      <strong>Classe 2 : </strong> 
                      commercial, agent commercial, recouvrement, collecteur, percepteur, marchand ambulant, chauffeur, artiste, acteur, enseignant technique,
                      couturier, coiffeur, cordonnier, boulanger, pâtissier, patissier, imprimeur, graveur, chimiste, laboratoire, bijoutier, joaillier.
                    </p>
                  ) : activityClass === '3' ? (
                    <p className="text-gray-700">
                      <strong>Classe 3 : </strong> 
                      electricien, mecanicien, deménageur, demenageur, manutentionnaire, livreur, conducteur engin, chauffeur engin, industrie, atelier, machine, _
                      veterinaire, professeur sport, entraineur, eleveur, gardien, vigile, agriculteur, forestier, biologiste.
                    </p>
                  ) : activityClass === '4' ? (
                    <p className="text-gray-700">
                      <strong>Classe 4 : </strong> 
                      charpentier, echafaudage, toit, travaux hauteur, boucher, abattoir, police, gendarme, maintien ordre, pompier, incendie, secours, btp, 
                      travaux publics, peintre batiment, ascenseur, dock, docker, menuisier, aciérie, carriere.
                    </p>
                  ) : (
                    <p className="text-gray-700">
                      <strong>Classe 5 : </strong> 
                      Abattage d’arbres, Mines Marins, pêcheurs en mer, Construction portuaire, Construction d’ouvrages d’art, Construction de barrages, 
                      Démolition d’immeubles, Electricien sur hautes tensions, Ouvriers de sociétés d’élagages, Extraction, fouille et terrassement, 
                      Dénivellation, Travail sur toits (couvertures, poseurs d'antennes…).
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Type d'assurance */}
            <div className="mb-6">
              <label className="block font-bold mb-4">Quel type d'assurance désirez-vous ?</label>
              {types && types.data ? (
                <div className="flex flex-wrap gap-4">
                  {types.data.map((t) => {
                    let uuid = SOLO_TYPE_UUID;
                    if (t.name === 'FAMILLE') {
                      uuid = FAMILLE_TYPE_UUID;
                    } else if (t.name === 'MONO FAMILLE') {
                      uuid = MONO_FAMILLE_TYPE_UUID;
                    } else if (t.name === 'SOLO') {
                      uuid = SOLO_TYPE_UUID;
                    }

                    return (
                      <label
                        key={t.key}
                        className={`px-6 py-3 rounded-full cursor-pointer transition ${assuranceType === uuid ? 'bg-secondary text-white' : 'border border-gray-800 hover:bg-gray-100'}`}
                      >
                        <input
                          type="radio"
                          name="assuranceType"
                          className="sr-only"
                          checked={assuranceType === uuid}
                          onChange={() => {
                            setAssuranceType(uuid);
                            if (errors.assuranceType) setErrors({...errors, assuranceType: ''});
                          }}
                        />
                        {t.name}
                      </label>
                    );
                  })}
                </div>
              ) : (
                <p>Chargement des types...</p>
              )}
              {errors.assuranceType && <p className="text-red-500 text-sm mt-2">{errors.assuranceType}</p>}

              {assuranceType && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                  {assuranceType === SOLO_TYPE_UUID ? (
                    <p className="text-gray-700"><strong>Offre Solo :</strong> Vous seul serez couvert par cette assurance.</p>
                  ) : assuranceType === MONO_FAMILLE_TYPE_UUID ? (
                    <p className="text-gray-700"><strong>Offre Mono Famille :</strong> Vous et vos enfants serez couverts par cette assurance.</p>
                  ) : (
                    <p className="text-gray-700"><strong>Offre Famille :</strong> Vous, votre conjoint(e) et vos enfants serez couverts par cette assurance.</p>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-center gap-4 mt-8 pt-6 border-t">
              <Link
                to="/step-1"
                className="rounded-full border border-gray-300 px-6 py-3 text-gray-700 flex items-center gap-2"
              >
                ← Retour
              </Link>
              <button
                onClick={handleContinue}
                className="rounded-full bg-primary px-6 py-3 text-secondary font-medium flex items-center gap-2"
              >
                Continuer →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
